import { JcLine, JcSection, JobCostingProjectSummary } from '../../models/job-costing.models';

const EPSILON = 0.005;

/**
 * Per-line figures (BRD §3). Mirrors the prototype's `lineCalc()`:
 * - Cost Variance = revised Job Cost Budget − (Actual + Accrued + open committed)
 * - Cost to Complete = revised Job Cost Budget − (Actual + Accrued), never floored
 * - projected cost feeds % complete only
 */
export interface JcLineTotals {
  /** Contract Price from the estimate (with markup), excluding change orders. */
  origSell: number;
  /** Job Cost Budget from the estimate (no markup), excluding change-order costs. */
  origBudget: number;
  /** Approved change orders, client $ (with markup). */
  coSell: number;
  /** Approved change orders, cost $ (no markup). */
  coCost: number;
  /** Current Contract Price = origSell + coSell. */
  sell: number;
  /** Revised Job Cost Budget = origBudget + coCost. */
  budget: number;
  /** Open POs entered on the line. */
  committed: number;
  /** PO amounts not yet covered by an applied actual or accrual; 0 on a complete line. */
  openCommitted: number;
  accrued: number;
  actual: number;
  pending: number;
  leftToBuy: number;
  variance: number;
  costToComplete: number;
  /** % complete denominator only — never shown as a figure. */
  projectedCost: number;
  over: boolean;
  unbudgeted: boolean;
}

export interface JcRollup {
  origSell: number;
  origBudget: number;
  coSell: number;
  coCost: number;
  sell: number;
  budget: number;
  committed: number;
  openCommitted: number;
  accrued: number;
  actual: number;
  pending: number;
  leftToBuy: number;
  projectedCost: number;
  variance: number;
  costToComplete: number;
  overCount: number;
  overAmount: number;
  /** actual ÷ projected cost. */
  completePct: number;
  /** @deprecated use coSell */
  changeOrderSell: number;
  /** @deprecated use coCost */
  changeOrderCost: number;
}

export type JcProjectTotals = JcRollup;

/** One portfolio row on the landing — the same parts as a project, summed by the API per project. */
export interface JcPortfolioMetrics {
  origSell: number;
  origBudget: number;
  coSell: number;
  coCost: number;
  sell: number;
  budget: number;
  committed: number;
  openCommitted: number;
  accrued: number;
  actual: number;
  pending: number;
  projectedCost: number;
  variance: number;
  costToComplete: number;
  overCount: number;
  reviewCount: number;
  reviewAmount: number;
}

export function calcLine(line: JcLine): JcLineTotals {
  const origSell = line.baselineBudget;
  const origBudget = line.estimatedCost;
  const coSell = line.changeOrderBudget;
  const coCost = line.changeOrderCost;
  const sell = origSell + coSell;
  const budget = origBudget + coCost;
  const committed = line.poCommitted ?? 0;
  const accrued = line.accrued ?? 0;
  const actual = line.actual;
  const pending = line.pendingAmount;
  const incurred = actual + accrued;
  // Until a PO link is recorded on every applied actual, the API reports open committed = committed.
  const openCommitted = line.isComplete ? 0 : (line.openCommitted ?? committed);

  if (line.isUnbudgeted) {
    return {
      origSell: 0, origBudget: 0, coSell: 0, coCost: 0, sell: 0, budget: 0,
      committed, openCommitted, accrued, actual, pending,
      leftToBuy: 0, variance: 0, costToComplete: 0, projectedCost: incurred,
      over: false, unbudgeted: true,
    };
  }

  const projectedCost = line.isComplete ? incurred : Math.max(budget, committed, incurred);
  const variance = budget - (incurred + openCommitted);
  const costToComplete = budget - incurred;
  const leftToBuy = line.isComplete ? 0 : Math.max(0, budget - Math.max(committed, incurred));
  return {
    origSell, origBudget, coSell, coCost, sell, budget,
    committed, openCommitted, accrued, actual, pending,
    leftToBuy, variance, costToComplete, projectedCost,
    over: variance < -EPSILON,
    unbudgeted: false,
  };
}

export function sumLines(lines: JcLine[]): JcRollup {
  const totals: JcRollup = emptyRollup();
  for (const line of lines) {
    const c = calcLine(line);
    totals.origSell += c.origSell;
    totals.origBudget += c.origBudget;
    totals.coSell += c.coSell;
    totals.coCost += c.coCost;
    totals.sell += c.sell;
    totals.budget += c.budget;
    totals.committed += c.committed;
    totals.openCommitted += c.openCommitted;
    totals.accrued += c.accrued;
    totals.actual += c.actual;
    totals.pending += c.pending;
    totals.leftToBuy += c.leftToBuy;
    totals.projectedCost += c.projectedCost;
    totals.variance += c.variance;
    totals.costToComplete += c.costToComplete;
    if (c.over) {
      totals.overCount++;
      totals.overAmount += -c.variance;
    }
  }
  totals.changeOrderSell = totals.coSell;
  totals.changeOrderCost = totals.coCost;
  totals.completePct = totals.projectedCost > 0 ? totals.actual / totals.projectedCost : 0;
  return totals;
}

export function projectTotals(sections: JcSection[]): JcProjectTotals {
  return sumLines(sections.flatMap(s => s.lines));
}

function emptyRollup(): JcRollup {
  return {
    origSell: 0, origBudget: 0, coSell: 0, coCost: 0, sell: 0, budget: 0,
    committed: 0, openCommitted: 0, accrued: 0, actual: 0, pending: 0,
    leftToBuy: 0, projectedCost: 0, variance: 0, costToComplete: 0,
    overCount: 0, overAmount: 0, completePct: 0,
    changeOrderSell: 0, changeOrderCost: 0,
  };
}

/** Landing-card figures from one project summary, using the band's formulas so a tile always equals the sum of its projects (FR-17). */
export function portfolioMetrics(summary: JobCostingProjectSummary): JcPortfolioMetrics {
  const origSell = summary.sell;
  const origBudget = summary.budgetCost;
  const coSell = summary.changeOrderSell;
  const coCost = summary.changeOrderCost;
  const sell = origSell + coSell;
  const budget = origBudget + coCost;
  const committed = summary.committed;
  const openCommitted = summary.openCommitted;
  const accrued = summary.accrued;
  const actual = summary.actual;
  const incurred = actual + accrued;
  const projectedCost = summary.projectedCost ?? Math.max(budget, committed, incurred);
  const variance = budget - (incurred + openCommitted);
  return {
    origSell, origBudget, coSell, coCost, sell, budget,
    committed, openCommitted, accrued, actual,
    pending: summary.pending ?? 0,
    projectedCost,
    variance,
    costToComplete: budget - incurred,
    overCount: summary.overCount ?? (variance < -EPSILON ? 1 : 0),
    reviewCount: summary.reviewCount ?? 0,
    reviewAmount: summary.reviewAmount ?? 0,
  };
}

/** Sums landing rows into the portfolio band (in-process projects only, by the caller). */
export function sumPortfolio(rows: JcPortfolioMetrics[]): JcPortfolioMetrics {
  return rows.reduce(
    (sum, row) => {
      sum.origSell += row.origSell;
      sum.origBudget += row.origBudget;
      sum.coSell += row.coSell;
      sum.coCost += row.coCost;
      sum.sell += row.sell;
      sum.budget += row.budget;
      sum.committed += row.committed;
      sum.openCommitted += row.openCommitted;
      sum.accrued += row.accrued;
      sum.actual += row.actual;
      sum.pending += row.pending;
      sum.projectedCost += row.projectedCost;
      sum.variance += row.variance;
      sum.costToComplete += row.costToComplete;
      sum.overCount += row.overCount;
      sum.reviewCount += row.reviewCount;
      sum.reviewAmount += row.reviewAmount;
      return sum;
    },
    {
      origSell: 0, origBudget: 0, coSell: 0, coCost: 0, sell: 0, budget: 0,
      committed: 0, openCommitted: 0, accrued: 0, actual: 0, pending: 0,
      projectedCost: 0, variance: 0, costToComplete: 0,
      overCount: 0, reviewCount: 0, reviewAmount: 0,
    } as JcPortfolioMetrics,
  );
}

/** The eight band tiles share one shape on the project page and the landing (D-71). */
export interface JcBandFigures {
  origSell: number;
  coSell: number;
  coCost: number;
  sell: number;
  origBudget: number;
  actual: number;
  committed: number;
  accrued: number;
  variance: number;
  costToComplete: number;
}

const currency = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Unsigned currency, e.g. `$1,234.50`. */
export function formatMoney(value: number): string {
  return '$' + currency.format(Math.abs(value));
}

/** Signed variance, e.g. `+$340.00` / `-$340.00` / `$0.00`. */
export function formatVariance(value: number): string {
  const sign = value < -EPSILON ? '-' : value > EPSILON ? '+' : '';
  return sign + formatMoney(value);
}

/** Signed only when negative, e.g. `-$340.00` / `$340.00` — Cost to Complete style. */
export function formatNegOnly(value: number): string {
  return (value < -EPSILON ? '-' : '') + formatMoney(value);
}

export function varianceClass(value: number): '' | 'jc-pos' | 'jc-neg' {
  return value < -EPSILON ? 'jc-neg' : value > EPSILON ? 'jc-pos' : '';
}

export function negClass(value: number): '' | 'jc-neg' {
  return value < -EPSILON ? 'jc-neg' : '';
}

export function formatPct(ratio: number): string {
  return Math.round(ratio * 100) + '%';
}
